import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError, type SessionListItem } from '../lib/api';
import { useAuth } from '../lib/auth';
import { VOICES } from '@ts/schema/types';
import { useState } from 'react';
import { enablePush } from '../lib/push';
import { isSupported as passkeySupported, registerPasskey } from '../lib/passkey';
import { ScreenHeader } from '../components/ScreenHeader';
import { Section } from '../components/Section';
import { IconButton } from '../components/IconButton';
import { IconXCircle } from '../components/icons';

const TRANSLATIONS = ['CSB', 'ESV', 'NIV', 'NLT', 'KJV', 'NASB'] as const;

export default function YouPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  if (auth.state !== 'signed-in') return null;
  const { me } = auth;
  const [voices, setVoices] = useState<string[]>(me.member.voices);
  const [pushState, setPushState] = useState<'idle' | 'enabling' | 'on' | 'error'>(
    'idle',
  );

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch('/me', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me'] }),
  });

  function toggleVoice(key: string) {
    const next = voices.includes(key)
      ? voices.filter((v) => v !== key)
      : [...voices, key];
    setVoices(next);
    patch.mutate({ voices: next });
  }

  return (
    <>
      <ScreenHeader
        title="Settings"
        subtitle={`${me.member.displayName} · ${me.family.name} · ${
          me.member.role === 'parent' ? 'Parent' : 'Youth'
        }`}
      />
      <div className="px-5 pt-4 pb-6">
        {me.isOwner && <AdminEntry />}
        <GettingStarted />

        <Section title="Family">
          <Row label="Invite code">
            <code className="font-mono text-oxblood text-lg tracking-wider">
              {me.family.inviteCode}
            </code>
          </Row>
          <Row label="Members">
            <div className="flex flex-wrap gap-2">
              {me.members.map((m) => (
                <span
                  key={m.id}
                  className="px-3 py-1 rounded-full border border-line text-sm text-soft"
                >
                  {m.displayName}
                </span>
              ))}
            </div>
          </Row>
          <Row label="Time zone">
            <span className="text-soft">{me.family.timezone}</span>
          </Row>
          {me.isFamilyOwner && (
            <div className="pt-3">
              <Link
                to="/household"
                className="inline-flex h-10 px-4 items-center rounded-lg border-2 border-oxblood text-oxblood text-sm font-medium"
              >
                Manage household
              </Link>
            </div>
          )}
        </Section>

        <Section title="Extended family">
          <p className="text-soft text-sm mb-3">
            Share selected things (photos, prayer requests) with grandparents, aunts,
            cousins — without dissolving what's just for our household. Each circle has
            its own Photos / Prayer / Chat under the Generations tab.
          </p>
          <Link
            to="/generations/manage"
            className="inline-flex h-10 px-4 items-center rounded-lg border-2 border-oxblood text-oxblood text-sm font-medium"
          >
            Manage generations
          </Link>
        </Section>

        <Section title="Reading">
          <Row label="Translation">
            <select
              defaultValue={me.member.translation}
              onChange={(e) => patch.mutate({ translation: e.target.value })}
              className="h-10 px-3 rounded-lg border border-line bg-card"
            >
              {TRANSLATIONS.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </Row>
        </Section>

        <Section title="Daily Voices">
          <p className="text-soft text-sm mb-3">
            Pick whose voices show up in <em>Go Deeper</em>. Only affects optional quotes
            — never the Scripture itself.
          </p>
          <div className="space-y-1">
            {VOICES.map((v) => (
              <label key={v.key} className="flex items-center justify-between py-2">
                <span className="text-ink">{v.name}</span>
                <input
                  type="checkbox"
                  checked={voices.includes(v.key)}
                  onChange={() => toggleVoice(v.key)}
                  className="accent-oxblood w-5 h-5"
                />
              </label>
            ))}
          </div>
        </Section>

        <Section title="Notifications">
          <Row label="Daily verse time">
            <input
              type="time"
              defaultValue={me.member.notifyTime ?? '06:30'}
              onBlur={(e) => patch.mutate({ notifyTime: e.target.value })}
              className="h-10 px-3 rounded-lg border border-line bg-card"
            />
          </Row>
          {me.member.role === 'youth' && (
            <Row label="School-hours quiet (8am–3pm)">
              <input
                type="checkbox"
                defaultChecked={me.member.schoolDnd}
                onChange={(e) => patch.mutate({ schoolDnd: e.target.checked })}
                className="accent-oxblood w-5 h-5"
              />
            </Row>
          )}
          <Row label="Push permission">
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={() => {
                  setPushState('enabling');
                  enablePush()
                    .then(() => setPushState('on'))
                    .catch(() => setPushState('error'));
                }}
                disabled={pushState === 'enabling'}
                className="h-10 px-4 rounded-lg border-2 border-oxblood text-oxblood text-sm font-medium disabled:opacity-50"
              >
                {pushState === 'enabling'
                  ? 'Enabling…'
                  : pushState === 'on'
                    ? 'Enabled'
                    : 'Enable on this device'}
              </button>
              {pushState === 'on' && (
                <span className="text-sage text-xs">Push enabled on this device.</span>
              )}
              {pushState === 'error' && (
                <span className="text-oxblood text-xs">
                  Couldn't enable push — check notification permissions.
                </span>
              )}
            </div>
          </Row>
        </Section>

        <Section title="Account">
          <EmailSection />
          <PasskeySection />
          <DeviceListSection />
          <div className="mt-6 pt-4 border-t border-line">
            <ChangePinForm />
            <div className="mt-4">
              <button
                onClick={async () => {
                  await api.post('/auth/logout');
                  window.location.href = '/';
                }}
                className="text-soft underline"
              >
                Sign out
              </button>
            </div>
          </div>
        </Section>
      </div>
    </>
  );
}

// ------------------------------------------------------- Getting Started
// Quick orientation for new users. Sits near the top of /you. Each
// topic is a collapsed <details> so the section stays compact for
// returning users; the wrapper has a brief invitation to expand.
function GettingStarted() {
  return (
    <Section title="Getting Started">
      <p className="text-soft text-sm mb-3">
        New here? Tap a topic to see how it works. The family tabs (Today, Memories, My
        Walk, Chat) stay inside the household. The Generations tab is where anything
        shared with extended family lives.
      </p>
      <div className="space-y-1">
        <Topic title="Today: the daily verse">
          The home tab. Each day brings a passage with a short principle, an
          age-appropriate "This Week, Try" action, an embedded devotional video, and a
          family discussion question. Listen to the audio reading from any device.
        </Topic>
        <Topic title="Memories: photos and captions">
          Upload from your phone's camera or library. Add a caption, tag the people who
          were there, and household members can like and comment. Photos sync to your
          phone too, so they keep working offline.
        </Topic>
        <Topic title="Generations: extended family, one circle at a time">
          Open{' '}
          <Link to="/generations" className="underline text-oxblood">
            Generations
          </Link>{' '}
          to switch between circles — grandparents, cousins, a small group from church.
          Each circle has its own Photos / Prayer / Chat sub-tabs, gated by what you
          turned on when you set it up. Posts here are visible to that circle and only
          that circle.
        </Topic>
        <Topic title="My Walk: prayer + Go Deeper videos">
          Your family prayer wall lives here. Add a request — short or long, everyone in
          the household sees it. When God answers, tap "Answered" and add a note. A
          theme-tagged video library sits below the prayer wall: tap a topic (prayer,
          parenting, suffering…) to find devotionals from our curated YouTube playlists.
        </Topic>
        <Topic title="Chat: messages, mentions, reactions">
          Real-time chat for the family. Type <code>@</code> to mention someone by name —
          they get a push notification. Long-press a message for Love, Reply, Copy, or
          Delete. Share a verse, prayer, photo, or encouragement straight from the compose
          row.
        </Topic>
        <Topic title="Alerts (bell in the header)">
          The bell up in the top-right shows when someone mentioned you or commented on
          something of yours. Tap it to see the list and jump straight to the source.
        </Topic>
        <Topic title="Settings (this page)">
          Pick your Bible translation, choose which devotional voices appear in
          <em> Go Deeper</em>, set the time you want the daily-verse notification, enable
          push on this device, change your PIN. Owners get an <strong>Admin</strong> link
          at the top for household/system management (usage, backups, playlists, videos,
          members).
        </Topic>
      </div>
    </Section>
  );
}

function Topic({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <details className="group border-b border-line last:border-b-0">
      <summary className="py-2 cursor-pointer list-none flex items-center justify-between text-ink">
        <span className="font-medium">{title}</span>
        <span
          aria-hidden
          className="text-soft text-xs transition-transform group-open:rotate-90"
        >
          ▸
        </span>
      </summary>
      <div className="pb-3 text-soft text-sm leading-relaxed">{children}</div>
    </details>
  );
}

// ---------------------------------------------------------- email section
// Phase 1: add + verify an email so the user has a recovery primitive
// beyond "remember the invite code AND your PIN." Signing in WITH
// email lands in Phase 2; this section is the bootstrap path.
function EmailSection() {
  const auth = useAuth();
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [codeDraft, setCodeDraft] = useState('');
  const [step, setStep] = useState<'idle' | 'enter' | 'verify'>('idle');
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const account = auth.state === 'signed-in' ? auth.me.account : undefined;

  async function startVerify() {
    setErr(null);
    setInfo(null);
    setPending(true);
    try {
      await api.post('/me/email/start', { email: emailDraft.trim() });
      setStep('verify');
      setInfo(
        `We sent a 6-digit code to ${emailDraft.trim()}. Enter it below within 10 minutes.`,
      );
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setPending(false);
    }
  }

  async function submitVerify() {
    setErr(null);
    setPending(true);
    try {
      await api.post('/me/email/verify', {
        email: emailDraft.trim(),
        code: codeDraft.trim(),
      });
      await qc.invalidateQueries({ queryKey: ['me'] });
      setStep('idle');
      setEmailDraft('');
      setCodeDraft('');
      setInfo('Email verified.');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setPending(false);
    }
  }

  if (!account) return null;

  return (
    <div className="mb-5">
      <h3 className="font-serif text-lg text-ink mb-1">Email</h3>
      {account.email && account.emailVerified ? (
        <div className="text-soft text-sm">
          <span className="text-ink">{account.email}</span> · verified.{' '}
          <button
            className="text-oxblood underline"
            onClick={() => {
              setStep('enter');
              setEmailDraft('');
            }}
          >
            Change
          </button>
        </div>
      ) : account.email ? (
        <div className="text-soft text-sm">
          <span className="text-ink">{account.email}</span> · not verified.{' '}
          <button
            className="text-oxblood underline"
            onClick={() => {
              setStep('enter');
              setEmailDraft(account.email!);
            }}
          >
            Verify now
          </button>
        </div>
      ) : step === 'idle' ? (
        <div className="text-soft text-sm">
          No email on file. Add one so we can send a code if you ever lose your PIN.{' '}
          <button className="text-oxblood underline" onClick={() => setStep('enter')}>
            Add email
          </button>
        </div>
      ) : null}

      {step === 'enter' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (emailDraft.trim()) void startVerify();
          }}
          className="mt-3 space-y-2"
        >
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={emailDraft}
            onChange={(e) => setEmailDraft(e.target.value)}
            placeholder="you@example.com"
            className="w-full h-11 px-3 rounded-lg border border-line bg-paper"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setStep('idle');
                setErr(null);
              }}
              className="text-soft text-sm px-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={pending || !emailDraft.trim()}
              className="h-10 px-4 rounded-lg bg-oxblood text-paper text-sm font-medium disabled:opacity-50"
            >
              {pending ? 'Sending…' : 'Send code'}
            </button>
          </div>
        </form>
      )}

      {step === 'verify' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (/^\d{6}$/.test(codeDraft.trim())) void submitVerify();
          }}
          className="mt-3 space-y-2"
        >
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={codeDraft}
            onChange={(e) => setCodeDraft(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="6-digit code"
            className="w-full h-11 px-3 rounded-lg border border-line bg-paper text-xl tracking-[0.3em] font-mono text-center"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setStep('enter');
                setCodeDraft('');
                setInfo(null);
              }}
              className="text-soft text-sm px-3"
            >
              Re-enter email
            </button>
            <button
              type="submit"
              disabled={pending || !/^\d{6}$/.test(codeDraft.trim())}
              className="h-10 px-4 rounded-lg bg-oxblood text-paper text-sm font-medium disabled:opacity-50"
            >
              {pending ? 'Verifying…' : 'Verify'}
            </button>
          </div>
        </form>
      )}

      {info && <p className="mt-2 text-sage text-sm">{info}</p>}
      {err && <p className="mt-2 text-oxblood text-sm">{err}</p>}
    </div>
  );
}

// ------------------------------------------------------ passkey section
// Phase 2: button is live. Registers a discoverable credential bound
// to the current device's authenticator (FaceID / TouchID / Windows
// Hello / security keys) so future sign-ins on this device can skip
// PIN entry. Re-running the flow on the same device adds another
// passkey (e.g., a phone + a YubiKey on the same account).
function PasskeySection() {
  const auth = useAuth();
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const supported = passkeySupported();
  const count = auth.state === 'signed-in' ? auth.me.account.passkeyCount : 0;

  async function add() {
    setErr(null);
    setInfo(null);
    setPending(true);
    try {
      // Derive a default label from the UA — the user can rename
      // later if we add a credentials list.
      const label = uaLabel(navigator.userAgent);
      await registerPasskey(label);
      await qc.invalidateQueries({ queryKey: ['me'] });
      setInfo('Passkey registered on this device.');
    } catch (e) {
      // Cancelled prompt throws too — distinguish for a friendlier
      // message.
      const msg = e instanceof Error ? e.message : String(e);
      if (/cancel|abort/i.test(msg)) {
        setInfo(null);
        setErr('Passkey setup was cancelled.');
      } else {
        setErr(msg);
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="mb-5">
      <h3 className="font-serif text-lg text-ink mb-1">Passkeys</h3>
      <p className="text-soft text-sm mb-2">
        {count === 0
          ? 'No passkeys yet. Register Face ID / Touch ID / a security key here so future sign-ins skip the PIN.'
          : `${count} ${count === 1 ? 'passkey' : 'passkeys'} registered. Add another on a different device for backup.`}
      </p>
      {!supported && (
        <p className="text-soft text-xs italic mb-2">
          This browser doesn’t support passkeys yet.
        </p>
      )}
      <button
        type="button"
        onClick={() => void add()}
        disabled={pending || !supported}
        className="h-10 px-4 rounded-lg bg-oxblood text-paper text-sm font-medium disabled:opacity-50"
      >
        {pending ? 'Setting up…' : 'Add passkey on this device'}
      </button>
      {info && <p className="mt-2 text-sage text-sm">{info}</p>}
      {err && <p className="mt-2 text-oxblood text-sm">{err}</p>}
    </div>
  );
}

/** Best-effort short label from a UA string for new credentials.
 *  Same shape as the session-label helper but kept inline since the
 *  server already does the equivalent for sessions. */
function uaLabel(ua: string): string {
  if (/iPhone/i.test(ua)) return /CriOS/i.test(ua) ? 'iPhone Chrome' : 'iPhone Safari';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua)) return 'Android';
  if (/Mac OS X/i.test(ua)) return /Chrome/i.test(ua) ? 'Mac Chrome' : 'Mac Safari';
  if (/Windows/i.test(ua)) return /Edg\//i.test(ua) ? 'Windows Edge' : 'Windows';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Browser';
}

// --------------------------------------------------- signed-in devices list
function DeviceListSection() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['me-sessions'],
    queryFn: () => api.get<{ sessions: SessionListItem[] }>('/me/sessions'),
    staleTime: 30_000,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/me/sessions/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me-sessions'] }),
  });

  if (!q.data) return null;
  const list = q.data.sessions;

  return (
    <div className="mb-5">
      <h3 className="font-serif text-lg text-ink mb-2">Signed-in devices</h3>
      {list.length === 0 ? (
        <p className="text-soft text-sm italic">
          No active sessions. (You’re probably reading this with a legacy cookie that
          pre-dates the session table — it’ll show up after your next sign-in.)
        </p>
      ) : (
        <ul className="space-y-2">
          {list.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between py-2 border-b border-line last:border-b-0 gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="text-ink">
                  {s.label ?? 'Unknown device'}{' '}
                  {s.current && (
                    <span className="text-xs text-sage font-medium ml-1">
                      this device
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted">
                  Last seen {new Date(s.lastSeenAt).toLocaleString()}
                </div>
              </div>
              {!s.current && (
                <IconButton
                  icon={<IconXCircle />}
                  label="Revoke session"
                  variant="destructive"
                  busy={revoke.isPending}
                  onClick={() => revoke.mutate(s.id)}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChangePinForm() {
  const [open, setOpen] = useState(false);
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      api.post('/me/pin', { currentPin: currentPin.trim(), newPin: newPin.trim() }),
    onSuccess: () => {
      setDone(true);
      setCurrentPin('');
      setNewPin('');
      setConfirmPin('');
      setErr(null);
    },
    onError: (e: Error) => setErr(e.message),
  });

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-oxblood underline text-sm">
        Change PIN
      </button>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setErr(null);
        setDone(false);
        if (newPin !== confirmPin) {
          setErr("The two new-PIN entries don't match.");
          return;
        }
        if (!/^\d{6,8}$/.test(newPin)) {
          setErr('New PIN must be 6–8 digits.');
          return;
        }
        mut.mutate();
      }}
      className="space-y-2"
    >
      <label className="block">
        <span className="text-soft text-xs">Current PIN</span>
        <input
          type="password"
          inputMode="numeric"
          autoComplete="current-password"
          pattern="\d{4,8}"
          value={currentPin}
          onChange={(e) => setCurrentPin(e.target.value)}
          className="block w-full mt-1 px-3 py-2 border border-line rounded-lg bg-paper text-ink"
        />
      </label>
      <label className="block">
        <span className="text-soft text-xs">New PIN (6–8 digits)</span>
        <input
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          pattern="\d{6,8}"
          value={newPin}
          onChange={(e) => setNewPin(e.target.value)}
          className="block w-full mt-1 px-3 py-2 border border-line rounded-lg bg-paper text-ink"
        />
      </label>
      <label className="block">
        <span className="text-soft text-xs">Confirm new PIN</span>
        <input
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          pattern="\d{6,8}"
          value={confirmPin}
          onChange={(e) => setConfirmPin(e.target.value)}
          className="block w-full mt-1 px-3 py-2 border border-line rounded-lg bg-paper text-ink"
        />
      </label>
      {err && <p className="text-oxblood text-sm">{err}</p>}
      {done && <p className="text-sage text-sm">PIN updated.</p>}
      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setErr(null);
            setDone(false);
          }}
          className="text-soft text-sm px-3 py-1"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={mut.isPending || !currentPin || !newPin || !confirmPin}
          className="bg-oxblood text-paper text-sm font-medium px-4 py-1.5 rounded-full disabled:opacity-50"
        >
          {mut.isPending ? 'Updating…' : 'Update PIN'}
        </button>
      </div>
    </form>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-soft text-sm">{label}</span>
      {children}
    </div>
  );
}

/** Owner-only entry to the Admin page. Sits near the top of Settings
 *  so the owner can jump to Usage / Backups / Playlists / Videos /
 *  Household without the full Settings page housing all of it. */
function AdminEntry() {
  return (
    <section className="card p-5 mb-4">
      <h2 className="font-serif text-xl text-ink mb-1">Admin</h2>
      <p className="text-soft text-sm mb-3">
        Household, content, and system. Usage, backups, playlists, videos, and member
        management live here.
      </p>
      <Link
        to="/admin"
        className="inline-flex h-10 px-4 items-center rounded-lg bg-oxblood text-paper text-sm font-medium"
      >
        Open admin →
      </Link>
    </section>
  );
}
